import React, { useRef } from 'react'
import { first, last } from 'lodash'
import makeStyles from '@mui/styles/makeStyles'
import {
  useEnableWhileAtBottom,
  useManualScrollTakeover,
  usePreserveScrollOnPrepend,
  useScrollToTarget,
} from './scrollHandlingHooks'
import useFeedOpenPosition from './useFeedOpenPosition'

type ItemWithIdentifier = {
  itemID: string
}

type ScrollableFeedContainerProps = {
  items: ItemWithIdentifier[]
  /** Anchor on the first unread item, landed on when opening a feed that has one. */
  firstUnreadRef?: React.RefObject<HTMLElement>
  /**
   * Re-positions the feed when the unread boundary moves. `undefined` until it resolves,
   * `null` once resolved to no boundary at all.
   */
  unreadBoundaryKey?: number | null
  children: React.ReactNode
}

// All three hooks below write the same scrollTop, so only one of them may be live at a time.
export default function ScrollableFeedContainer({
  items,
  firstUnreadRef,
  unreadBoundaryKey,
  children,
}: ScrollableFeedContainerProps) {
  const areaRef = useRef<HTMLDivElement>(null)
  const chatBottom = useRef<HTMLDivElement>(null)

  const hasScrolledManually = useManualScrollTakeover(areaRef)

  // Older messages get loaded above the viewport — keep the same message under the eye
  usePreserveScrollOnPrepend(areaRef, [first(items)?.itemID], { skip: !hasScrolledManually.current })

  // A new message arrives while the feed is open — follow it, when already at the bottom
  const setScrollToTargetEnabled = useScrollToTarget(chatBottom, true, [last(items)?.itemID], {
    skip: !hasScrolledManually.current,
  })
  useEnableWhileAtBottom(areaRef, setScrollToTargetEnabled)

  // The feed is opened — position it on the first unread message, or the newest if none
  useFeedOpenPosition({
    areaRef,
    newestRef: chatBottom,
    firstUnreadRef,
    unreadBoundaryKey,
    itemCount: items.length,
    oldestItemID: first(items)?.itemID,
    newestItemID: last(items)?.itemID,
    hasScrolledManually,
    stopFollowing: () => setScrollToTargetEnabled(false),
  })

  const styles = useStyles()

  return (
    <div ref={areaRef} className={styles.scrollableFeedContainer}>
      {children}
      <div ref={chatBottom} />
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  scrollableFeedContainer: {
    flex: 1,
    padding: theme.spacing(3),
    overflowX: 'hidden',
  },
}))
